import React from "react";
import { Box, Typography } from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { QUIZ } from "@/theme/quizTokens";
import { quizAsset } from "@/components/quiz/shared/quizAssets";

/**
 * Presentational sections for the redesigned Review & confirm step (Figma 16v2,
 * mobile 2050:1672 / desktop 1588:2207): header block, money-back banner and the
 * full-bleed marketing bands below the order card.
 */

/** Breaks out of the QuizShell content column to span the full viewport width. */
export function FullBleed({ children, sx }: { children: React.ReactNode; sx?: any }) {
  return (
    <Box
      sx={{
        width: "100vw",
        position: "relative",
        left: "50%",
        ml: "-50vw",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

/** Green 94% roundel overlapping the hero band (bottom-right). */
export function NinetyFourBadge() {
  return (
    <Box
      component="img"
      src={quizAsset("review-94-badge")}
      alt="94% verified by All About Dog Food"
      sx={{
        position: "absolute",
        // Desktop band image already has the roundel baked in — mobile only.
        display: { xs: "block", md: "none" },
        width: 96,
        height: "auto",
        right: 18,
        bottom: -18,
        transform: "rotate(8deg)",
        pointerEvents: "none",
        zIndex: 2,
      }}
    />
  );
}

/** REVIEW heading + subtitle + green Cancel Anytime chip. */
export function ReviewHeader() {
  return (
    <Box sx={{ textAlign: "center", pt: { xs: "26px", md: "40px" }, pb: { xs: "28px", md: "36px" } }}>
      <Typography
        component="h1"
        sx={{
          fontFamily: QUIZ.font.display,
          fontWeight: 900,
          textTransform: "uppercase",
          color: QUIZ.color.brown,
          fontSize: { xs: "44px", md: "64px" },
          lineHeight: 1,
          mb: { xs: "14px", md: "18px" },
        }}
      >
        Review
      </Typography>
      <Typography
        sx={{
          fontFamily: QUIZ.font.body,
          fontSize: { xs: "18px", md: "20px" },
          color: QUIZ.color.grey,
          maxWidth: { xs: 330, md: 700 },
          mx: "auto",
          lineHeight: 1.35,
          mb: { xs: "18px", md: "22px" },
        }}
      >
        Review your box below – you can still go back to adjust meals and treats.
      </Typography>
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          bgcolor: "#b5e561",
          borderRadius: "6px",
          px: "12px",
          py: "5px",
        }}
      >
        <CalendarMonthIcon sx={{ fontSize: 16, color: QUIZ.color.brown }} />
        <Typography
          sx={{
            fontFamily: QUIZ.font.button,
            fontWeight: 700,
            fontSize: "13px",
            color: QUIZ.color.brown,
          }}
        >
          Cancel Anytime
        </Typography>
      </Box>
    </Box>
  );
}

/** Blue "Money back guarantee..." banner. */
export function MoneyBackBanner() {
  return (
    <Box
      sx={{
        bgcolor: QUIZ.color.blue,
        borderRadius: "10px",
        px: { xs: "18px", md: "22px" },
        py: { xs: "16px", md: "18px" },
        mt: { xs: "20px", md: "26px" },
      }}
    >
      <Typography
        sx={{
          fontFamily: QUIZ.font.label,
          fontWeight: 700,
          fontSize: { xs: "17px", md: "18px" },
          lineHeight: 1.35,
          color: QUIZ.color.dark,
        }}
      >
        Money-back guarantee if your dog doesn't love it.
      </Typography>
    </Box>
  );
}

/** "ARRIVING FROZEN FOR ULTIMATE FRESHNESS" ice band. */
export function FrozenBand() {
  return (
    <FullBleed sx={{ mt: { xs: "40px", md: "64px" } }}>
      <Box
        sx={{
          position: "relative",
          height: { xs: 270, md: 320 },
          backgroundImage: `url(${quizAsset("review-frozen-band")})`,
          backgroundSize: "cover",
          backgroundPosition: { xs: "center 70%", md: "center 32%" },
          display: "flex",
          justifyContent: "center",
          pt: { xs: "26px", md: "44px" },
        }}
      >
        <Typography
          sx={{
            fontFamily: QUIZ.font.display,
            fontWeight: 900,
            textTransform: "uppercase",
            color: QUIZ.color.dark,
            textAlign: "center",
            fontSize: { xs: "27px", md: "46px" },
            lineHeight: 1.1,
            maxWidth: { xs: 340, md: 720 },
          }}
        >
          Arriving frozen for ultimate freshness
        </Typography>
      </Box>
    </FullBleed>
  );
}

export type ReviewQuote = { title?: string; quote: string; author: string; date?: string };

export const REVIEW_QUOTES: ReviewQuote[] = [
  {
    quote:
      "We have a particularly fussy dog and have worked through a buck of the more premium dog foods settling in butternut box, although lots of dinners would still go uneaten. Switched to paws on plates and she goes straight in! Plus, the cardboard packaging is better! Sam and the team have done a great job here, would definitely reccommend",
    author: "Laurence",
  },
  {
    title: "Best dog food",
    quote:
      "My two cocker spaniels have been on this food for around 8 months now. Have tried most diets after my dog started with allergies and this gives the best quality of ingredients (allergic to offal found in butternut box and similar) and varety of flavours he can still have (cant have chicken/beef). Wish subscription page was easier to edit eg if dogs weight changes etc as have to do a new subcription for them. Recent prob with order (dpd prob) and rhey rectified quickly even though not their fault. My dog was literally crying with anticipation after a day and half of not having it.",
    author: "Lib Norman",
    date: "Mar 21, 2026",
  },
  {
    title: "Thank you so much",
    quote:
      "Thank you so much , I have contemplated the type of food for my cocker spaniel puppy. Thought I would give this a try after reading about the ingredients so switched from kibble. Winnie loves these meal she literally dances when I bring out the tubs. Stools more formed and really pleased with this. Whilst quite pricey worth the money so far. Thank you",
    author: "Nicola Robinson",
    date: "Apr 9, 2026",
  },
];

/** Trustpilot logo lockup — green star + wordmark. */
function TrustpilotLogo() {
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
      <Box
        sx={{
          width: { xs: 34, md: 38 },
          height: { xs: 34, md: 38 },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <svg viewBox="0 0 24 24" width="100%" height="100%" fill="#00B67A">
          <path d="M12 1.5l2.9 6.6 7.1.6-5.4 4.7 1.6 7.1L12 16.7l-6.2 3.8 1.6-7.1L2 8.7l7.1-.6L12 1.5z" />
        </svg>
      </Box>
      <Typography
        sx={{
          fontFamily: "'Helvetica Neue', Arial, sans-serif",
          fontWeight: 700,
          fontSize: { xs: "30px", md: "34px" },
          color: "#191919",
          letterSpacing: "-0.5px",
          lineHeight: 1,
        }}
      >
        Trustpilot
      </Typography>
    </Box>
  );
}

/** Green Trustpilot 5-star row. */
export function StarsRow({ size = 26 }: { size?: number }) {
  return (
    <Box sx={{ display: "flex", gap: "3px" }}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Box
          key={i}
          sx={{
            width: size,
            height: size,
            bgcolor: "#00B67A",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <svg viewBox="0 0 24 24" width="78%" height="78%" fill="#fff">
            <path d="M12 2l2.9 6.26L21.5 9.27l-4.75 4.14L18.18 20 12 16.9 5.82 20l1.43-6.59L2.5 9.27l6.6-1.01L12 2z" />
          </svg>
        </Box>
      ))}
    </Box>
  );
}

function QuoteBlock({ q, center }: { q: ReviewQuote; center?: boolean }) {
  return (
    <Box sx={{ textAlign: center ? "center" : "left", minWidth: 0 }}>
      {!center && <StarsRow size={20} />}
      {q.title && (
        <Typography
          sx={{
            fontFamily: QUIZ.font.body,
            fontWeight: 700,
            fontSize: "16px",
            color: QUIZ.color.dark,
            mt: center ? 0 : "20px",
            mb: "8px",
          }}
        >
          {q.title}
        </Typography>
      )}
      <Typography
        sx={{
          fontFamily: QUIZ.font.body,
          fontSize: { xs: "17px", md: "16px" },
          lineHeight: 1.45,
          color: QUIZ.color.grey,
          mb: { xs: "22px", md: "26px" },
          mt: q.title ? 0 : center ? 0 : "20px",
        }}
      >
        {q.quote}
      </Typography>
      <Typography
        sx={{
          fontFamily: QUIZ.font.label,
          fontWeight: 700,
          fontSize: { xs: "19px", md: "17px" },
          color: QUIZ.color.brown,
        }}
      >
        {q.author}
      </Typography>
      {q.date && (
        <Typography sx={{ fontFamily: QUIZ.font.body, fontSize: "14px", color: QUIZ.color.grey, mt: "4px" }}>
          {q.date}
        </Typography>
      )}
    </Box>
  );
}

/** "What you say" Trustpilot card — one centred quote on mobile, two columns on desktop. */
export function WhatYouSayCard() {
  return (
    <Box sx={{ position: "relative", mt: { xs: "56px", md: "72px" }, mb: { xs: "24px", md: "48px" } }}>
      {/* 4.8/5 heart roundel overlapping the card's top-right corner */}
      <Box
        component="img"
        src={quizAsset("review-48-badge")}
        alt="4.8 out of 5 Trustpilot reviews"
        sx={{
          position: "absolute",
          width: { xs: 118, md: 190 },
          height: "auto",
          right: { xs: -12, md: -60 },
          top: { xs: -46, md: -56 },
          zIndex: 2,
          pointerEvents: "none",
        }}
      />

      <Box
        sx={{
          bgcolor: "#e8dfd3",
          borderRadius: "24px",
          px: { xs: "26px", md: "62px" },
          pt: { xs: "56px", md: "48px" },
          pb: { xs: "36px", md: "44px" },
        }}
      >
        {/* Header lockup */}
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "16px", mb: { xs: "28px", md: "36px" } }}>
          <Typography
            sx={{
              fontFamily: QUIZ.font.label,
              fontWeight: 700,
              fontSize: { xs: "22px", md: "24px" },
              color: "#f2695c",
            }}
          >
            What you say
          </Typography>
          <TrustpilotLogo />
          <StarsRow size={30} />
        </Box>

        {/* Mobile: single centred quote */}
        <Box sx={{ display: { xs: "block", md: "none" } }}>
          <Box sx={{ textAlign: "center" }}>
            <QuoteBlock q={REVIEW_QUOTES[0]} center />
          </Box>
        </Box>

        {/* Desktop: two review columns */}
        <Box sx={{ display: { xs: "none", md: "flex" }, gap: "64px", justifyContent: "center" }}>
          <Box sx={{ flex: 1, maxWidth: 430 }}>
            <QuoteBlock q={REVIEW_QUOTES[1]} />
          </Box>
          <Box sx={{ flex: 1, maxWidth: 430 }}>
            <QuoteBlock q={REVIEW_QUOTES[2]} />
          </Box>
        </Box>

        {/* Pager arrows (decorative) */}
        <Box sx={{ display: "flex", justifyContent: "center", gap: "18px", mt: { xs: "26px", md: "10px" } }}>
          {[ArrowBackIcon, ArrowForwardIcon].map((Icon, i) => (
            <Box
              key={i}
              sx={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                border: `1.5px solid ${QUIZ.color.brown}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: QUIZ.color.brown,
              }}
            >
              <Icon sx={{ fontSize: 20 }} />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

/** Brown OUR PROMISE band: icons row + green verified strip + display copy. */
export function OurPromiseBand() {
  const items = [
    { icon: "icon-promise-cancel", label: "Cancel anytime" },
    { icon: "icon-promise-moneyback", label: "Money-back guarantee" },
    { icon: "icon-promise-freshness", label: "100% freshness" },
  ];

  return (
    <FullBleed>
      <Box sx={{ bgcolor: "#3f2b21", px: { xs: "38px", md: 0 }, py: { xs: "56px", md: "72px" } }}>
        <Box sx={{ maxWidth: 900, mx: "auto" }}>
          <Typography
            sx={{
              fontFamily: QUIZ.font.display,
              fontWeight: 900,
              textTransform: "uppercase",
              color: QUIZ.color.cream,
              textAlign: "center",
              fontSize: { xs: "40px", md: "56px" },
              lineHeight: 1,
              mb: { xs: "34px", md: "44px" },
            }}
          >
            Our promise
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "center", gap: { xs: "26px", md: "120px" }, mb: { xs: "40px", md: "56px" } }}>
            {items.map((it) => (
              <Box key={it.icon} sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "14px", width: { xs: 92, md: 150 } }}>
                <Box
                  component="img"
                  src={quizAsset(it.icon)}
                  alt=""
                  sx={{ height: { xs: 44, md: 56 }, width: "auto" }}
                />
                <Typography
                  sx={{
                    fontFamily: QUIZ.font.label,
                    fontWeight: 700,
                    fontSize: { xs: "15px", md: "18px" },
                    color: QUIZ.color.cream,
                    textAlign: "center",
                    lineHeight: 1.25,
                  }}
                >
                  {it.label}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Verified strip */}
          <Box
            sx={{
              bgcolor: "#b5e561",
              borderRadius: "4px",
              py: { xs: "10px", md: "14px" },
              px: "16px",
              textAlign: "center",
              mb: { xs: "38px", md: "40px" },
            }}
          >
            <Typography
              sx={{
                fontFamily: QUIZ.font.label,
                fontWeight: 700,
                fontSize: { xs: "17px", md: "22px" },
                color: "#3f2b21",
              }}
            >
              Verified 94% by All About Dog Food
            </Typography>
          </Box>

          <Typography
            sx={{
              fontFamily: QUIZ.font.label,
              fontWeight: 700,
              fontSize: { xs: "24px", md: "22px" },
              lineHeight: 1.35,
              color: QUIZ.color.cream,
              textAlign: "center",
              maxWidth: { xs: "100%", md: 560 },
              mx: "auto",
            }}
          >
            Choose Paws for a happier, healthier dog with more energy and a shinier coat.
          </Typography>
        </Box>
      </Box>
    </FullBleed>
  );
}

/** Light-blue "OVER 1,000,000 BOWLS LICKED CLEAN" band with the collie photo. */
export function BowlsLickedBand() {
  return (
    <FullBleed
      sx={{
        // Cancel the shell's bottom padding (content gap + fixed-footer reservation)
        // so the band runs flush behind the sticky footer, as in the Figma.
        mb: {
          xs: "calc(-24px - var(--quizFooterH, 70px))",
          md: "calc(-48px - var(--quizFooterH, 76px))",
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          background: "linear-gradient(180deg, #d9f2fb 0%, #aee6f8 78%, #7fd7f3 78%, #7fd7f3 100%)",
          minHeight: { xs: 420, md: 640 },
          overflow: "hidden",
        }}
      >
        <Box sx={{ maxWidth: 1240, mx: "auto", px: { xs: "38px", md: "60px" } }}>
          <Typography
            sx={{
              fontFamily: QUIZ.font.display,
              fontWeight: 900,
              textTransform: "uppercase",
              color: QUIZ.color.dark,
              fontSize: { xs: "34px", md: "58px" },
              lineHeight: 1.05,
              // Line structure comes from explicit <br />s (Over 1,000,000 / bowls / licked / clean)
              whiteSpace: "nowrap",
              pt: { xs: "64px", md: "180px" },
              position: "relative",
              zIndex: 1,
            }}
          >
            Over 1,000,000 <br /> bowls <br />
            licked <br />
            clean
          </Typography>
        </Box>
        <Box
          component="img"
          src={quizAsset("review-bowls-collie")}
          alt=""
          sx={{
            position: "absolute",
            right: { xs: -30, md: "6%" },
            bottom: 0,
            width: { xs: 300, md: 560 },
            height: "auto",
            pointerEvents: "none",
          }}
        />
      </Box>
    </FullBleed>
  );
}
